import { el, mount } from "../utils/dom";
import { renderPageHeader } from "../components/pageHeader";
import { renderLoadingState, renderErrorState, renderEmptyState } from "../components/states";
import { renderStatusText } from "../components/statusText";
import { renderAbsoluteTime } from "../components/dateTime";
import { confirmAction } from "../components/confirmDialog";
import { showToast } from "../components/toast";
import { HELP } from "../components/tooltip";
import { navigateBack } from "../app/navigation";
import {
  createGroup,
  deleteGroup,
  getEntityColumns,
  getGroup,
  getGroupServices,
  patchGroup,
} from "../services/admin";
import { ApiError } from "../services/apiClient";
import { ENTITY_ORDER, entityLabel } from "../utils/format";
import type { AuthGroup, ServiceAccount } from "../types/api";

export class GroupEditorPage {
  private body = el("div", {}, [renderLoadingState("Loading group…")]);
  private group: AuthGroup | null = null;
  private services: ServiceAccount[] = [];
  private validColumns: string[] = [];
  private selectedColumns = new Set<string>();
  private name = "";
  private entity = "";
  private saving = false;
  private navigate: (path: string) => void = () => undefined;

  constructor(private id: string | null) {}

  mount(outlet: HTMLElement, navigate: (path: string) => void): void {
    this.navigate = navigate;
    mount(
      outlet,
      el("div", {}, [
        renderPageHeader({
          title: this.id ? "Edit access group" : "Create access group",
          titleHelp: HELP.accessGroup,
          subtitle: "Pick one data type and the fields services in this group are allowed to see.",
          back: { fallbackPath: "/groups", navigate },
          actions: [
            el(
              "button",
              { className: "btn btn-ghost btn-sm", type: "button", onclick: () => void this.load() },
              ["Refresh"]
            ),
          ],
        }),
        this.body,
      ])
    );
    void this.load();
  }

  destroy(): void {}

  private async load(): Promise<void> {
    mount(this.body, renderLoadingState("Loading group…"));
    try {
      if (this.id) {
        this.group = await getGroup(this.id);
        this.name = this.group.name;
        this.entity = this.group.entity;
        this.selectedColumns = new Set(this.group.columns);
        [this.validColumns, this.services] = await Promise.all([
          getEntityColumns(this.entity),
          getGroupServices(this.id),
        ]);
      } else {
        this.group = null;
        this.services = [];
        this.validColumns = [];
        this.selectedColumns.clear();
        this.name = "";
        this.entity = "";
      }
      this.render();
    } catch (err) {
      mount(this.body, renderErrorState(errorMessage(err), { onRetry: () => void this.load() }));
    }
  }

  private render(): void {
    const nameInput = el("input", {
      className: "input",
      type: "text",
      value: this.name,
      "aria-label": "Group name",
      oninput: (ev: Event) => {
        this.name = (ev.target as HTMLInputElement).value;
      },
    });

    const entitySelect = el(
      "select",
      {
        className: "input input--mono",
        disabled: Boolean(this.id),
        "aria-label": "Data type",
        onchange: (ev: Event) => {
          this.entity = (ev.target as HTMLSelectElement).value;
          this.selectedColumns.clear();
          void this.loadColumns();
        },
      },
      [
        el("option", { value: "", selected: !this.entity }, ["Select a data type"]),
        ...ENTITY_ORDER.map((entity) =>
          el("option", { value: entity, selected: entity === this.entity }, [entityLabel(entity)])
        ),
      ]
    );

    mount(
      this.body,
      el("div", {}, [
        el("div", { className: "panel panel--standard" }, [
          el("p", { className: "panel__title" }, ["Group"]),
          el("p", { className: "panel__description" }, [
            this.id
              ? "The data type cannot be changed after the group is created."
              : "Choose the one data type this group applies to.",
          ]),
          field("Name", nameInput),
          field("Data type", entitySelect),
          this.group
            ? el("div", { className: "next-cron mt-6" }, [
                detailRow("Created at", renderAbsoluteTime(this.group.createdAt)),
                detailRow("Updated at", renderAbsoluteTime(this.group.updatedAt)),
              ])
            : null,
        ]),
        this.renderColumnsPanel(),
        this.id ? this.renderDependenciesPanel() : null,
        el("div", { className: "form-actions form-actions--between" }, [
          this.id
            ? el(
                "button",
                {
                  className: "btn btn-danger-ghost btn-sm",
                  type: "button",
                  onclick: () => void this.remove(),
                },
                ["Delete access group"]
              )
            : el("span"),
          el("div", { className: "form-actions" }, [
            el(
              "button",
              {
                className: "btn btn-ghost btn-sm",
                type: "button",
                onclick: () => navigateBack(this.navigate, "/groups"),
              },
              ["Cancel"]
            ),
            el(
              "button",
              {
                className: "btn btn-primary btn-sm",
                type: "button",
                disabled: this.saving,
                onclick: () => void this.save(),
              },
              [this.saving ? "Saving…" : this.id ? "Save changes" : "Create group"]
            ),
          ]),
        ]),
      ])
    );
  }

  private renderColumnsPanel(): HTMLElement {
    let content: HTMLElement;
    if (!this.entity) {
      content = renderEmptyState("Select a data type to see its available fields.");
    } else if (!this.validColumns.length) {
      content = renderEmptyState("No fields are available for this data type.");
    } else {
      content = el("div", { className: "stack stack--gap-4", dataset: { toggleList: "columns" } }, [
        this.selectAllRow(),
        el(
          "div",
          { className: "column-picker" },
          this.validColumns.map((column) => this.columnRow(column))
        ),
      ]);
    }

    return el("div", { className: "panel panel--standard" }, [
      el("p", { className: "panel__title" }, [
        "Column permissions (",
        el("span", { dataset: { selectedCount: "" } }, [String(this.selectedColumns.size)]),
        " selected)",
      ]),
      el("p", { className: "panel__description" }, [
        "Only the fields you select are visible to services that use this group.",
      ]),
      content,
    ]);
  }

  private syncColumnSelectionUi(): void {
    const list = this.body.querySelector("[data-toggle-list='columns']");
    if (!list) return;

    const allSelected =
      this.validColumns.length > 0 &&
      this.validColumns.every((column) => this.selectedColumns.has(column));
    list.querySelector("[data-select-all]")?.setAttribute("aria-checked", String(allSelected));
    const label = list.querySelector("[data-select-all-label]");
    if (label) label.textContent = allSelected ? "Deselect all" : "Select all";

    for (const toggle of list.querySelectorAll<HTMLElement>("[data-item-id]")) {
      const column = toggle.dataset.itemId ?? "";
      toggle.setAttribute("aria-checked", String(this.selectedColumns.has(column)));
    }

    const count = this.body.querySelector("[data-selected-count]");
    if (count) count.textContent = String(this.selectedColumns.size);
  }

  private selectAllRow(): HTMLElement {
    const allSelected =
      this.validColumns.length > 0 &&
      this.validColumns.every((column) => this.selectedColumns.has(column));

    return el("div", { className: "row row--gap-4" }, [
      el("button", {
        className: "toggle",
        type: "button",
        role: "switch",
        "aria-label": "Select all columns",
        "aria-checked": String(allSelected),
        dataset: { selectAll: "" },
        onclick: () => {
          const currentlyAll =
            this.validColumns.length > 0 &&
            this.validColumns.every((column) => this.selectedColumns.has(column));
          if (currentlyAll) this.selectedColumns.clear();
          else this.selectedColumns = new Set(this.validColumns);
          this.syncColumnSelectionUi();
        },
      }),
      el("span", { dataset: { selectAllLabel: "" } }, [allSelected ? "Deselect all" : "Select all"]),
    ]);
  }

  private columnRow(column: string): HTMLElement {
    const selected = this.selectedColumns.has(column);
    return el("div", { className: "row row--gap-4" }, [
      el("button", {
        className: "toggle",
        type: "button",
        role: "switch",
        "aria-label": `Allow ${column}`,
        "aria-checked": String(selected),
        dataset: { itemId: column },
        onclick: () => {
          if (this.selectedColumns.has(column)) this.selectedColumns.delete(column);
          else this.selectedColumns.add(column);
          this.syncColumnSelectionUi();
        },
      }),
      el("span", { className: "mono" }, [column]),
    ]);
  }

  private renderDependenciesPanel(): HTMLElement {
    const content = this.services.length
      ? el(
          "div",
          { className: "stack stack--gap-4" },
          this.services.map((service) =>
            el("div", { className: "next-cron__row" }, [
              el("span", {}, [service.name]),
              renderStatusText(service.isActive ? "Active" : "Inactive", service.isActive ? "success" : "muted"),
            ])
          )
        )
      : renderEmptyState("No service accounts are assigned to this group.");

    return el("div", { className: "panel panel--standard" }, [
      el("p", { className: "panel__title" }, [`Assigned services (${this.services.length})`]),
      el("p", { className: "panel__description" }, [
        this.services.length
          ? "These service accounts currently use this access group. Deleting the group will remove it from them."
          : "Nothing is using this access group.",
      ]),
      content,
    ]);
  }

  private async loadColumns(): Promise<void> {
    if (!this.entity) {
      this.validColumns = [];
      this.render();
      return;
    }
    mount(this.body, renderLoadingState("Loading entity columns…"));
    try {
      this.validColumns = await getEntityColumns(this.entity);
      this.render();
    } catch (err) {
      mount(this.body, renderErrorState(errorMessage(err), { onRetry: () => void this.loadColumns() }));
    }
  }

  private async save(): Promise<void> {
    const name = this.name.trim();
    if (!name) {
      showToast("Group name is required.", "error");
      return;
    }
    if (!this.entity) {
      showToast("Select a data type.", "error");
      return;
    }
    if (!this.selectedColumns.size) {
      showToast("Select at least one column.", "error");
      return;
    }

    this.saving = true;
    this.render();
    try {
      const columns = Array.from(this.selectedColumns);
      const saved = this.id
        ? await patchGroup(this.id, { name, columns })
        : await createGroup({ name, entity: this.entity, columns });
      showToast(this.id ? "Access group updated." : "Access group created.", "success");
      if (!this.id) {
        this.navigate("/groups");
        return;
      }
      this.group = saved;
      this.name = saved.name;
      this.selectedColumns = new Set(saved.columns);
    } catch (err) {
      showToast(errorMessage(err), "error");
    } finally {
      this.saving = false;
      if (this.id) this.render();
    }
  }

  private async remove(): Promise<void> {
    if (!this.id) return;
    try {
      this.services = await getGroupServices(this.id);
      this.render();
    } catch (err) {
      showToast(errorMessage(err), "error");
      return;
    }

    const confirmed = await confirmAction({
      title: `Delete ${this.group?.name ?? "this access group"}?`,
      message: deleteConfirmMessage(this.services),
      confirmLabel: "Delete access group",
      danger: true,
    });
    if (!confirmed) return;

    try {
      await deleteGroup(this.id);
      showToast("Access group deleted.", "success");
      this.navigate("/groups");
    } catch (err) {
      showToast(errorMessage(err), "error");
    }
  }
}

function deleteConfirmMessage(services: ServiceAccount[]): HTMLElement {
  const intro = el("p", {}, [
    "This permanently deletes the access group and detaches it from any assigned services. This cannot be undone.",
  ]);
  if (!services.length) {
    return el("div", { className: "stack stack--gap-3" }, [
      intro,
      el("p", {}, ["No service accounts currently use this access group."]),
    ]);
  }
  return el("div", { className: "stack stack--gap-3" }, [
    intro,
    el("p", {}, [
      `${services.length} service account${services.length === 1 ? "" : "s"} currently use this access group:`,
    ]),
    el(
      "ul",
      { className: "confirm-service-list" },
      services.map((service) => el("li", {}, [service.name]))
    ),
  ]);
}

function field(label: string, control: HTMLElement): HTMLElement {
  return el("div", { className: "field field--spaced" }, [
    el("span", { className: "field__label" }, [label]),
    control,
  ]);
}

function detailRow(label: string, value: HTMLElement): HTMLElement {
  return el("div", { className: "next-cron__row" }, [
    el("span", { className: "next-cron__label" }, [label]),
    value,
  ]);
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return "Something went wrong loading this access group.";
}
